import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/auth';

interface SmartLoadingContent {
  verses: string[];
  tips: string[];
  messages: string[];
}

const MORNING_VERSES = [
  "This is the day the Lord has made; let us rejoice and be glad in it. - Psalm 118:24",
  "The steadfast love of the Lord never ceases; his mercies never come to an end. - Lamentations 3:22",
  "Commit your way to the Lord; trust in him, and he will act. - Psalm 37:5"
];

const EVENING_VERSES = [
  "Be still, and know that I am God. - Psalm 46:10",
  "Cast all your anxiety on him because he cares for you. - 1 Peter 5:7",
  "Come to me, all who labor and are heavy laden, and I will give you rest. - Matthew 11:28"
];

const DEFAULT_VERSES = [
  "For I know the plans I have for you, declares the Lord. - Jeremiah 29:11",
  "The Lord your God is with you, the Mighty Warrior who saves. - Zephaniah 3:17",
  "In all your ways submit to him, and he will make your paths straight. - Proverbs 3:6"
];

const LOADING_TIPS = [
  "Tip: Use the search feature to find specific Bible passages quickly",
  "Tip: Create prayer requests to connect with your community",
  "Tip: Bookmark your favorite verses for easy access",
  "Tip: Try the audio feature to listen to prayers and verses",
  "Tip: Join prayer circles to pray with others"
];

const PERSONALIZED_MESSAGES = [
  "Preparing your spiritual journey...",
  "Loading your prayer community...",
  "Gathering wisdom from Scripture...",
  "Connecting hearts in prayer...",
  "Building bridges of faith..."
];

export const useSmartLoading = () => {
  const { user, profile } = useAuth();
  const [currentVerseIndex, setCurrentVerseIndex] = useState(0);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);

  // Determine time-based content
  const timeBasedVerses = useMemo(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return MORNING_VERSES;
    if (hour >= 18 && hour < 24) return EVENING_VERSES;
    return DEFAULT_VERSES;
  }, []);

  // Get personalized loading message
  const personalizedMessage = useMemo(() => {
    const username = profile?.first_name || user?.email?.split('@')[0] || 'friend';
    const messages = [
      `Welcome back, ${username}`,
      `Preparing your experience, ${username}`,
      `Good to see you again, ${username}`
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  }, [user, profile]);

  // Cycle through verses
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentVerseIndex(prev => (prev + 1) % timeBasedVerses.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [timeBasedVerses.length]);

  // Cycle through tips
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTipIndex(prev => (prev + 1) % LOADING_TIPS.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const getContextualMessage = (progress: number) => {
    if (progress < 30) return "Starting your spiritual journey...";
    if (progress < 60) return "Loading sacred content...";
    if (progress < 90) return "Preparing prayer community...";
    return "Almost ready...";
  };

  return {
    currentVerse: timeBasedVerses[currentVerseIndex],
    currentTip: LOADING_TIPS[currentTipIndex],
    personalizedMessage,
    getContextualMessage,
    verses: timeBasedVerses,
    tips: LOADING_TIPS,
    messages: PERSONALIZED_MESSAGES
  };
};